import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  TrendingUp, Eye, Heart, IndianRupee, Users, BarChart3,
  ChevronDown, ChevronRight, ExternalLink, Megaphone, Calendar, Zap, Link2, AlertTriangle
} from 'lucide-react';
import { PortfolioFilter } from '../components/PortfolioFilter';
import MarketingDashboard from '../components/marketing/MarketingDashboard';
import MarketingPerformanceReview from '../components/marketing/MarketingPerformanceReview';
import { fmtDateIST } from '../lib/tz';


const fmt = v => {
  if (v == null || isNaN(v)) return '—';
  const n = Number(v);
  if (n === 0) return '₹0';
  if (Math.abs(n) > 0 && Math.abs(n) < 1) return '< ₹1';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};
const num = v => v != null && !isNaN(v) ? Number(v).toLocaleString('en-IN') : '—';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const LT_LABELS = {
  retainership: 'Retainership / PR',
  meta_ads: 'Meta Ads',
  google_ads: 'Google Ads',
  swiggy_cpc: 'Swiggy CPC',
  swiggy_ads: 'Swiggy Ads',
  zomato_cpc: 'Zomato CPC',
  zomato_ads: 'Zomato Ads',
  hoardings: 'Hoardings',
};

function SummaryMetric({ label, value, color, testId }) {
  return (
    <div className="min-w-0 flex-1 text-center px-2 py-1.5" data-testid={testId}>
      <p className="text-[10px] text-[#5C6B64] uppercase tracking-wide truncate">{label}</p>
      <p className={`text-sm font-bold ${color || 'text-[#1A2421]'} truncate`}>{value}</p>
    </div>
  );
}

function LinkBadge({ url, label }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-[10px] text-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5 hover:bg-[color:var(--color-primary)]/10 px-2 py-1 rounded transition-colors"
      data-testid={`link-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <ExternalLink className="w-2.5 h-2.5" />{label}
    </a>
  );
}

function EfficiencyMetrics({ data }) {
  const growth = data.follower_growth ?? (data.closing_followers - data.opening_followers);
  const cpf = data.total_spend > 0 && growth > 0 ? (data.total_spend / growth) : null;
  const cpv = data.total_spend > 0 && data.total_views > 0 ? (data.total_spend / data.total_views * 1000) : null;
  const cpe = data.total_spend > 0 && data.total_engagement > 0 ? (data.total_spend / data.total_engagement) : null;

  return (
    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#E2E8DF]">
      <div className="p-2 bg-[color:var(--color-primary)]/5 rounded-lg text-center">
        <p className="text-[9px] text-[#5C6B64] uppercase">Cost / Follower</p>
        <p className="text-sm font-bold text-[color:var(--color-primary)]" data-testid="eff-cpf">
          {cpf != null ? fmt(cpf) : growth === 0 ? '—' : 'Organic'}
        </p>
      </div>
      <div className="p-2 bg-[color:var(--color-primary)]/5 rounded-lg text-center">
        <p className="text-[9px] text-[#5C6B64] uppercase">Cost / 1K Views</p>
        <p className="text-sm font-bold text-[color:var(--color-primary)]" data-testid="eff-cpv">
          {cpv != null ? fmt(cpv) : data.total_views === 0 ? '—' : 'Organic'}
        </p>
      </div>
      <div className="p-2 bg-[color:var(--color-primary)]/5 rounded-lg text-center">
        <p className="text-[9px] text-[#5C6B64] uppercase">Cost / Engagement</p>
        <p className="text-sm font-bold text-[color:var(--color-primary)]" data-testid="eff-cpe">
          {cpe != null ? fmt(cpe) : data.total_engagement === 0 ? '—' : 'Organic'}
        </p>
      </div>
    </div>
  );
}

/* ───────── LONG-TERM DETAIL ───────── */
function LongtermDetail({ expenses }) {
  const items = (expenses || []).filter(lt => lt.cost > 0);
  if (items.length === 0) return <p className="text-xs text-[#5C6B64] italic py-2">No long-term expenses</p>;
  return (
    <div className="space-y-1.5" data-testid="longterm-detail">
      {items.map((lt, i) => (
        <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-[#F9F9F8] rounded text-xs">
          <span className="text-[#1A2421] font-medium">{LT_LABELS[lt.category] || lt.category}</span>
          <div className="flex items-center gap-3 text-right">
            {lt.start_date && lt.end_date && (
              <span className="text-[10px] text-[#5C6B64]">{lt.start_date} → {lt.end_date}</span>
            )}
            <span className="text-[#5C6B64]">{fmt(lt.cost)}</span>
            <span className="text-[#2D6A4F] font-semibold min-w-[60px]">{fmt(lt.prorated)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───────── ADHOC DETAIL ───────── */
function AdhocDetail({ expenses, customCosts }) {
  const ad = expenses || {};
  const rows = [
    { label: 'Influencers', cost: ad.influencers?.cost, link: ad.influencers?.post_link },
    { label: 'Paper Ads', cost: ad.paper_ads?.cost, link: ad.paper_ads?.link },
    { label: 'LSM', cost: ad.lsm?.cost },
    { label: 'Sponsorships', cost: ad.sponsorships?.cost },
    { label: 'Others', cost: ad.others?.cost },
  ].filter(r => (r.cost || 0) > 0);
  const customs = (customCosts || []).filter(c => c.amount > 0);
  if (rows.length === 0 && customs.length === 0) return <p className="text-xs text-[#5C6B64] italic py-2">No adhoc expenses</p>;
  return (
    <div className="space-y-1.5" data-testid="adhoc-detail">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-[#F9F9F8] rounded text-xs">
          <div className="flex items-center gap-2">
            <span className="text-[#1A2421] font-medium">{r.label}</span>
            {r.link && <LinkBadge url={r.link} label="View" />}
          </div>
          <span className="text-[#1A2421] font-semibold">{fmt(r.cost)}</span>
        </div>
      ))}
      {customs.map((c, i) => (
        <div key={`c-${i}`} className="flex items-center justify-between py-1.5 px-2 bg-[#F9F9F8] rounded text-xs">
          <span className="text-[#1A2421] font-medium">{c.label} <span className="text-[10px] text-[#5C6B64]">(custom)</span></span>
          <span className="text-[#1A2421] font-semibold">{fmt(c.amount)}</span>
        </div>
      ))}
    </div>
  );
}

/* ═══════════ OUTLET CARD (Weekly View) ═══════════ */
function OutletCard({ data }) {
  const [expanded, setExpanded] = useState(false);
  const [showLtDetail, setShowLtDetail] = useState(false);
  const [showAdDetail, setShowAdDetail] = useState(false);

  const growth = data.follower_growth ?? (data.closing_followers - data.opening_followers);
  const cpf = data.total_spend > 0 && growth > 0 ? data.total_spend / growth : null;
  const cpe = data.total_spend > 0 && data.total_engagement > 0 ? data.total_spend / data.total_engagement : null;
  const cpv = data.total_spend > 0 && data.total_views > 0 ? data.total_spend / data.total_views * 1000 : null;
  const ltTotal = data.longterm_total || 0;
  const adTotal = data.adhoc_total || 0;
  const ctTotal = data.custom_total || 0;
  const hasWarning = (data.total_engagement || 0) === 0;

  return (
    <Card className={`border-[#E2E8DF] shadow-sm overflow-hidden ${hasWarning ? 'border-l-2 border-l-[#D4A017]' : ''}`}
      data-testid={`mkt-card-${data.outlet_id}`}>
      {/* ── SUMMARY ROW (always visible) ── */}
      <button type="button" onClick={() => setExpanded(e => !e)}
        className="w-full text-left hover:bg-[#F9F9F8]/50 transition-colors" data-testid={`mkt-expand-${data.outlet_id}`}>
        <CardHeader className="pb-0 pt-3 px-4 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-semibold text-[#1A2421]">{data.outlet_name}</CardTitle>
            <Badge variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64]">
              {data.week_start} → {data.week_end}
            </Badge>
          </div>
          {expanded
            ? <ChevronDown className="w-4 h-4 text-[#5C6B64]" />
            : <ChevronRight className="w-4 h-4 text-[#5C6B64]" />
          }
        </CardHeader>
        <CardContent className="pt-2 pb-3 px-4">
          <div className="flex items-stretch divide-x divide-[#E2E8DF] bg-[#F9F9F8] rounded-lg overflow-hidden">
            <SummaryMetric label="Total Spend" value={fmt(data.total_spend)} testId="sum-spend" />
            <SummaryMetric label="CPF" value={cpf != null ? fmt(cpf) : '—'} testId="sum-cpf" />
            <SummaryMetric label="CPE" value={cpe != null ? fmt(cpe) : '—'} testId="sum-cpe" />
            <SummaryMetric label="CP1K Views" value={cpv != null ? fmt(cpv) : '—'} testId="sum-cpv" />
            <SummaryMetric label="LT | Ad"
              value={`${fmt(ltTotal)} | ${fmt(adTotal)}`}
              color="text-[#5C6B64]" testId="sum-lt-ad" />
          </div>
        </CardContent>
      </button>

      {/* ── EXPAND VIEW ── */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#E2E8DF] pt-3">
          {/* Performance */}
          <div>
            <p className="text-[10px] font-medium text-[#5C6B64] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Performance
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Followers</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.opening_followers)} → {num(data.closing_followers)}</p>
                <p className={`text-[10px] font-medium ${growth >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                  {growth >= 0 ? '+' : ''}{num(growth)}
                </p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Posts</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.num_posts)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Total Views</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.total_views)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Highest Views</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.highest_views ?? data.max_views)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Engagement</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.total_engagement)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Highest Engage</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.highest_engagement ?? data.max_engagement)}</p>
              </div>
            </div>
          </div>

          {/* Spend Split */}
          <div>
            <p className="text-[10px] font-medium text-[#5C6B64] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <IndianRupee className="w-3 h-3" /> Spend Split
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Long-term</p>
                <p className="text-sm font-bold text-[#1A2421]">{fmt(ltTotal)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Adhoc</p>
                <p className="text-sm font-bold text-[#1A2421]">{fmt(adTotal)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Custom</p>
                <p className="text-sm font-bold text-[#1A2421]">{fmt(ctTotal)}</p>
              </div>
            </div>
          </div>

          {/* Links */}
          {(data.best_post_link || data.paid_ads_link) && (
            <div>
              <p className="text-[10px] font-medium text-[#5C6B64] uppercase tracking-wide mb-1.5 flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Content Links
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <LinkBadge url={data.best_post_link} label="Best Post" />
                <LinkBadge url={data.paid_ads_link} label="Paid Ads" />
              </div>
            </div>
          )}

          {/* Efficiency */}
          <EfficiencyMetrics data={data} />

          {/* ── DETAIL (collapsible) ── */}
          <div className="space-y-2 pt-2">
            <Collapsible open={showLtDetail} onOpenChange={setShowLtDetail}>
              <CollapsibleTrigger asChild>
                <button type="button"
                  className="flex items-center justify-between w-full py-2 px-3 bg-[color:var(--color-primary)]/5 rounded-lg hover:bg-[color:var(--color-primary)]/10 transition-colors text-left text-xs"
                  data-testid="detail-longterm-toggle">
                  <span className="flex items-center gap-1.5 font-medium text-[#1A2421]">
                    <Calendar className="w-3 h-3 text-[color:var(--color-primary)]" /> Long-term Breakdown
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-[#5C6B64] transition-transform ${showLtDetail ? 'rotate-180' : ''}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-1.5">
                <LongtermDetail expenses={data.longterm_expenses} />
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={showAdDetail} onOpenChange={setShowAdDetail}>
              <CollapsibleTrigger asChild>
                <button type="button"
                  className="flex items-center justify-between w-full py-2 px-3 bg-[color:var(--color-primary)]/5 rounded-lg hover:bg-[color:var(--color-primary)]/10 transition-colors text-left text-xs"
                  data-testid="detail-adhoc-toggle">
                  <span className="flex items-center gap-1.5 font-medium text-[#1A2421]">
                    <Zap className="w-3 h-3 text-[color:var(--color-primary)]" /> Adhoc Breakdown
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-[#5C6B64] transition-transform ${showAdDetail ? 'rotate-180' : ''}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-1.5">
                <AdhocDetail expenses={data.adhoc_expenses} customCosts={data.custom_costs} />
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ═══════════ MTD CARD ═══════════ */
function MtdCard({ data, month, year }) {
  const [expanded, setExpanded] = useState(false);

  const growth = data.follower_growth ?? 0;
  const cpf = data.total_spend > 0 && growth > 0 ? data.total_spend / growth : null;
  const cpe = data.total_spend > 0 && data.total_engagement > 0 ? data.total_spend / data.total_engagement : null;
  const cpv = data.total_spend > 0 && data.total_views > 0 ? data.total_spend / data.total_views * 1000 : null;
  const budget = data.budget || 0;
  const utilization = data.budget_utilization || (budget > 0 ? Math.round(data.total_spend / budget * 100) : 0);
  const overBudget = budget > 0 && data.total_spend > budget;
  const hasWarning = (data.total_engagement || 0) === 0;

  return (
    <Card className={`border-[#E2E8DF] shadow-sm overflow-hidden ${overBudget ? 'border-l-2 border-l-[#C45A41]' : hasWarning ? 'border-l-2 border-l-[#D4A017]' : ''}`}
      data-testid={`mtd-card-${data.outlet_id}`}>
      {/* ── SUMMARY ── */}
      <button type="button" onClick={() => setExpanded(e => !e)}
        className="w-full text-left hover:bg-[#F9F9F8]/50 transition-colors" data-testid={`mtd-expand-${data.outlet_id}`}>
        <CardHeader className="pb-0 pt-3 px-4 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-semibold text-[#1A2421]">
              {data.outlet_name} — {MONTHS[month - 1]} {year}
            </CardTitle>
            <Badge variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64]">
              {data.weeks} week{data.weeks !== 1 ? 's' : ''}
            </Badge>
            {overBudget && (
              <Badge className="text-[10px] bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20" data-testid="over-budget-badge">
                Over Budget
              </Badge>
            )}
          </div>
          {expanded
            ? <ChevronDown className="w-4 h-4 text-[#5C6B64]" />
            : <ChevronRight className="w-4 h-4 text-[#5C6B64]" />
          }
        </CardHeader>
        <CardContent className="pt-2 pb-3 px-4">
          <div className="flex items-stretch divide-x divide-[#E2E8DF] bg-[#F9F9F8] rounded-lg overflow-hidden">
            <SummaryMetric label="Total Spend" value={fmt(data.total_spend)} testId="mtd-sum-spend" />
            <SummaryMetric label="Budget" value={budget > 0 ? fmt(budget) : '—'} testId="mtd-sum-budget" />
            <SummaryMetric label="Utilization"
              value={budget > 0 ? `${utilization}%` : '—'}
              color={overBudget ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}
              testId="mtd-sum-util" />
            <SummaryMetric label="CPF" value={cpf != null ? fmt(cpf) : '—'} testId="mtd-sum-cpf" />
            <SummaryMetric label="CPE" value={cpe != null ? fmt(cpe) : '—'} testId="mtd-sum-cpe" />
            <SummaryMetric label="CP1K Views" value={cpv != null ? fmt(cpv) : '—'} testId="mtd-sum-cpv" />
          </div>
        </CardContent>
      </button>

      {/* ── EXPAND ── */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#E2E8DF] pt-3">
          {/* Performance */}
          <div>
            <p className="text-[10px] font-medium text-[#5C6B64] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Performance
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Followers</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.opening_followers)} → {num(data.closing_followers)}</p>
                <p className={`text-[10px] font-medium ${growth >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                  {growth >= 0 ? '+' : ''}{num(growth)}
                </p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Posts</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.total_posts)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Views</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.total_views)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Engagement</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.total_engagement)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg">
                <p className="text-[10px] text-[#5C6B64]">Highest Engage</p>
                <p className="text-xs font-bold text-[#1A2421]">{num(data.max_engagement)}</p>
              </div>
            </div>
          </div>

          {/* Spend Split */}
          <div>
            <p className="text-[10px] font-medium text-[#5C6B64] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <IndianRupee className="w-3 h-3" /> Spend Split
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Long-term</p>
                <p className="text-sm font-bold text-[#1A2421]">{fmt(data.longterm_total)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Adhoc</p>
                <p className="text-sm font-bold text-[#1A2421]">{fmt(data.adhoc_total)}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Custom</p>
                <p className="text-sm font-bold text-[#1A2421]">{fmt(data.custom_total)}</p>
              </div>
            </div>
          </div>

          {/* Budget */}
          <div>
            <p className="text-[10px] font-medium text-[#5C6B64] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <BarChart3 className="w-3 h-3" /> Budget
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Budget</p>
                <p className="text-sm font-bold text-[#1A2421]">{budget > 0 ? fmt(budget) : '—'}</p>
              </div>
              <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64]">Actual</p>
                <p className="text-sm font-bold text-[#1A2421]">{fmt(data.total_spend)}</p>
              </div>
              <div className={`p-2 rounded-lg text-center ${overBudget ? 'bg-[#C45A41]/5' : 'bg-[#F9F9F8]'}`}>
                <p className="text-[10px] text-[#5C6B64]">Utilization</p>
                <p className={`text-sm font-bold ${overBudget ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}>
                  {budget > 0 ? `${utilization}%` : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Efficiency */}
          <EfficiencyMetrics data={data} />
        </div>
      )}
    </Card>
  );
}

/* ═══════════ PORTFOLIO SUMMARY CARD ═══════════ */
function PortfolioSummaryCard({ data, label }) {
  const totals = data.reduce((acc, d) => ({
    total_spend: acc.total_spend + (d.total_spend || 0),
    follower_growth: acc.follower_growth + ((d.follower_growth ?? (d.closing_followers - d.opening_followers)) || 0),
    total_views: acc.total_views + (d.total_views || 0),
    total_engagement: acc.total_engagement + (d.total_engagement || 0),
    total_posts: acc.total_posts + (d.num_posts || d.total_posts || 0),
    longterm_total: acc.longterm_total + (d.longterm_total || 0),
    adhoc_total: acc.adhoc_total + (d.adhoc_total || 0),
    custom_total: acc.custom_total + (d.custom_total || 0),
    budget: acc.budget + (d.budget || 0),
  }), { total_spend: 0, follower_growth: 0, total_views: 0, total_engagement: 0, total_posts: 0, longterm_total: 0, adhoc_total: 0, custom_total: 0, budget: 0 });

  const cpf = totals.total_spend > 0 && totals.follower_growth > 0 ? totals.total_spend / totals.follower_growth : null;
  const cpe = totals.total_spend > 0 && totals.total_engagement > 0 ? totals.total_spend / totals.total_engagement : null;
  const cpv = totals.total_spend > 0 && totals.total_views > 0 ? totals.total_spend / totals.total_views * 1000 : null;
  const utilPct = totals.budget > 0 ? Math.round(totals.total_spend / totals.budget * 100) : 0;
  const overBudget = totals.budget > 0 && totals.total_spend > totals.budget;

  // Outlier detection
  const zeroEngagement = data.filter(d => (d.total_engagement || 0) === 0).length;
  const overBudgetOutlets = data.filter(d => d.budget > 0 && (d.total_spend || 0) > d.budget).length;

  return (
    <Card className="border-[color:var(--color-primary)] shadow-sm bg-[color:var(--color-primary)]/5 mb-4" data-testid="portfolio-summary">
      <CardHeader className="pb-1 pt-3 px-4">
        <CardTitle className="text-sm font-semibold text-[color:var(--color-primary)] flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Portfolio {label} — {data.length} outlet{data.length !== 1 ? 's' : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-3 px-4">
        <div className="flex items-stretch divide-x divide-[var(--color-primary)]/15 bg-white/70 rounded-lg overflow-hidden mb-2">
          <SummaryMetric label="Total Spend" value={fmt(totals.total_spend)} testId="pf-spend" />
          <SummaryMetric label="Growth" value={totals.follower_growth >= 0 ? `+${num(totals.follower_growth)}` : num(totals.follower_growth)}
            color={totals.follower_growth >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'} testId="pf-growth" />
          <SummaryMetric label="Views" value={num(totals.total_views)} testId="pf-views" />
          <SummaryMetric label="CPF" value={cpf != null ? fmt(cpf) : '—'} testId="pf-cpf" />
          <SummaryMetric label="CPE" value={cpe != null ? fmt(cpe) : '—'} testId="pf-cpe" />
          <SummaryMetric label="CP1K" value={cpv != null ? fmt(cpv) : '—'} testId="pf-cpv" />
          {totals.budget > 0 && (
            <SummaryMetric label="Budget Used"
              value={`${utilPct}%`}
              color={overBudget ? 'text-[#C45A41]' : utilPct >= 75 ? 'text-[#D4A017]' : 'text-[#2D6A4F]'}
              testId="pf-util" />
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#5C6B64]">
          <span>LT: {fmt(totals.longterm_total)}</span>
          <span>Adhoc: {fmt(totals.adhoc_total)}</span>
          <span>Custom: {fmt(totals.custom_total)}</span>
          <span>Posts: {num(totals.total_posts)}</span>
        </div>
        {(zeroEngagement > 0 || overBudgetOutlets > 0) && (
          <div className="flex items-center gap-3 mt-1.5">
            {overBudgetOutlets > 0 && (
              <Badge className="text-[10px] bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20" data-testid="pf-over-budget-alert">
                <AlertTriangle className="w-2.5 h-2.5 mr-1" />{overBudgetOutlets} over budget
              </Badge>
            )}
            {zeroEngagement > 0 && (
              <Badge className="text-[10px] bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20" data-testid="pf-zero-engage-alert">
                <AlertTriangle className="w-2.5 h-2.5 mr-1" />{zeroEngagement} with 0 engagement
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ═══════════ MAIN PAGE ═══════════ */
export default function AdminMarketing() {
  const { getHeaders, API } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [outlets, setOutlets] = useState([]);
  const [filterOutlet, setFilterOutlet] = useState('all');
  const [trendOutlet, setTrendOutlet] = useState('');
  const [weeklyData, setWeeklyData] = useState([]);
  const [mtdData, setMtdData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portfolioId, setPortfolioId] = useState('');
  const [budgets, setBudgets] = useState([]);
  const [budgetEdits, setBudgetEdits] = useState({});
  const [savingBudget, setSavingBudget] = useState({});
  // Marketing spinner bug — Budgets must own its own loader. The shared
  // `loading` was originally used by weekly / MTD / trend; `loadBudgets`
  // never touched it, so the page-level spinner (initial value true)
  // stuck permanently above the Budgets tab.
  const [budgetsLoading, setBudgetsLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/outlets`, { headers: getHeaders() })
      .then(res => { setOutlets(res.data); if (res.data.length) setTrendOutlet(res.data[0].id); })
      .catch(() => {});
  }, [API, getHeaders]);

  const loadWeekly = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/marketing`;
      const params = [];
      if (filterOutlet !== 'all') params.push(`outlet_id=${filterOutlet}`);
      if (portfolioId) params.push(`portfolio_id=${portfolioId}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url, { headers: getHeaders() });
      setWeeklyData(res.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [API, getHeaders, filterOutlet, portfolioId]);

  const loadMtd = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/marketing/mtd?year=${year}&month=${month}`;
      if (filterOutlet !== 'all') url += `&outlet_id=${filterOutlet}`;
      if (portfolioId) url += `&portfolio_id=${portfolioId}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setMtdData(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [API, getHeaders, year, month, filterOutlet, portfolioId]);

  const loadTrend = useCallback(async () => {
    if (!trendOutlet) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/marketing/trend?outlet_id=${trendOutlet}`, { headers: getHeaders() });
      setTrendData(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [API, getHeaders, trendOutlet]);

  const loadBudgets = useCallback(async () => {
    setBudgetsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/marketing/budgets?year=${year}&month=${month}`, { headers: getHeaders() });
      setBudgets(res.data);
      setBudgetEdits({});
    } catch {}
    finally { setBudgetsLoading(false); }
  }, [API, getHeaders, year, month]);

  useEffect(() => { if (tab === 'weekly') loadWeekly(); }, [tab, loadWeekly]);
  useEffect(() => { if (tab === 'mtd') loadMtd(); }, [tab, loadMtd]);
  useEffect(() => { if (tab === 'trend') loadTrend(); }, [tab, loadTrend]);
  useEffect(() => { if (tab === 'budgets') loadBudgets(); }, [tab, loadBudgets]);

  const handleBudgetSave = async (outletId) => {
    const val = parseFloat(budgetEdits[outletId]);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid budget amount'); return; }
    setSavingBudget(p => ({ ...p, [outletId]: true }));
    try {
      await axios.post(`${API}/admin/marketing/budget-override?outlet_id=${outletId}&year=${year}&month=${month}`,
        { budget: val }, { headers: getHeaders() });
      toast.success('Budget saved');
      loadBudgets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingBudget(p => ({ ...p, [outletId]: false })); }
  };

  return (
    <div data-testid="admin-marketing-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Marketing</h2>
            <p className="text-sm text-[#5C6B64] mt-1">Marketing performance &amp; campaign analytics</p>
          </div>
          <PortfolioFilter value={portfolioId} onChange={setPortfolioId} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <TabsList className="bg-[#F3F5F2] p-0.5 rounded-lg w-fit">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="mkt-dashboard-tab">📊 Dashboard</TabsTrigger>
            <TabsTrigger value="review" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="mkt-review-tab">📋 Performance Review</TabsTrigger>
            <TabsTrigger value="weekly" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="mkt-weekly-tab">Weekly</TabsTrigger>
            <TabsTrigger value="mtd" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="mkt-mtd-tab">MTD</TabsTrigger>
            <TabsTrigger value="trend" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="mkt-trend-tab">Trend</TabsTrigger>
            <TabsTrigger value="budgets" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="mkt-budgets-tab">Budgets</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2 flex-wrap">
            {tab !== 'trend' && (
              <Select value={filterOutlet} onValueChange={setFilterOutlet}>
                <SelectTrigger className="w-36 h-8 border-[#E2E8DF] text-sm" data-testid="mkt-outlet-filter">
                  <SelectValue placeholder="All Outlets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Outlets</SelectItem>
                  {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {tab === 'mtd' && (
              <>
                <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
                  <SelectTrigger className="w-20 h-8 border-[#E2E8DF] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
                  <SelectTrigger className="w-20 h-8 border-[#E2E8DF] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
            {tab === 'trend' && (
              <Select value={trendOutlet} onValueChange={setTrendOutlet}>
                <SelectTrigger className="w-48 h-8 border-[#E2E8DF] text-sm" data-testid="mkt-trend-outlet">
                  <SelectValue placeholder="Select outlet" />
                </SelectTrigger>
                <SelectContent>
                  {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Page-level spinner ONLY for legacy tabs that share the
            `loading` flag (weekly / mtd / trend). Dashboard, Review AND
            Budgets each own their own loader, so we suppress this one
            there. Without excluding "budgets", the initial `loading=true`
            leaked a permanent spinner over the Budgets tab even though
            `loadBudgets` never controlled that state. */}
        {loading && tab !== 'dashboard' && tab !== 'review' && tab !== 'budgets' && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
          </div>
        )}

        {/* Dashboard View — ITER286 */}
        <TabsContent value="dashboard">
          <MarketingDashboard
            mode="admin"
            portfolioId={portfolioId}
            outletId={filterOutlet !== 'all' ? filterOutlet : ''}
          />
        </TabsContent>

        {/* ITER-marketing-pass2 — Performance Review + printable summary */}
        <TabsContent value="review">
          <MarketingPerformanceReview
            portfolioId={portfolioId}
            outletId={filterOutlet !== 'all' ? filterOutlet : ''}
            outletName={filterOutlet !== 'all' ? (outlets.find(o => o.id === filterOutlet)?.name || '') : ''}
          />
        </TabsContent>

        {/* Weekly View */}
        <TabsContent value="weekly">
          {!loading && (weeklyData.length === 0 ? (
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-10 text-center">
                <Megaphone className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
                <p className="text-sm text-[#5C6B64]">No marketing data submitted yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {weeklyData.length > 1 && <PortfolioSummaryCard data={weeklyData} label="Weekly" />}
              {weeklyData.map(d => <OutletCard key={d.outlet_id} data={d} />)}
            </div>
          ))}
        </TabsContent>

        {/* MTD View */}
        <TabsContent value="mtd">
          {!loading && (mtdData.length === 0 ? (
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-10 text-center">
                <p className="text-sm text-[#5C6B64]">No data for {MONTHS[month - 1]} {year}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {mtdData.length > 1 && <PortfolioSummaryCard data={mtdData} label={`MTD ${MONTHS[month - 1]} ${year}`} />}
              {mtdData.map(d => <MtdCard key={d.outlet_id} data={d} month={month} year={year} />)}
            </div>
          ))}
        </TabsContent>

        {/* Trend View */}
        <TabsContent value="trend">
          {!loading && (trendData.length === 0 ? (
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-10 text-center">
                <p className="text-sm text-[#5C6B64]">No trend data available for this outlet</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-[#E2E8DF] shadow-sm overflow-hidden" data-testid="trend-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#1A2421] flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[color:var(--color-primary)]" /> Week-on-Week Trend
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left text-[#1A2421]">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                        <th className="px-3 py-2 font-medium">Week</th>
                        <th className="px-3 py-2 font-medium text-right">Followers</th>
                        <th className="px-3 py-2 font-medium text-right">Growth</th>
                        <th className="px-3 py-2 font-medium text-right">Posts</th>
                        <th className="px-3 py-2 font-medium text-right">Views</th>
                        <th className="px-3 py-2 font-medium text-right">Engage</th>
                        <th className="px-3 py-2 font-medium text-right">Spend</th>
                        <th className="px-3 py-2 font-medium text-right">LT</th>
                        <th className="px-3 py-2 font-medium text-right">Ad</th>
                        <th className="px-3 py-2 font-medium text-right">CPF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendData.map((d, i) => {
                        const growth = d.follower_growth ?? (d.closing_followers - d.opening_followers);
                        const cpf = d.total_spend > 0 && growth > 0 ? Math.round(d.total_spend / growth) : null;
                        return (
                          <tr key={i} className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                            data-testid={`trend-row-${i}`}>
                            <td className="px-3 py-2 text-xs font-medium whitespace-nowrap">{d.week_start?.slice(5)}</td>
                            <td className="px-3 py-2 text-right text-xs">{num(d.closing_followers)}</td>
                            <td className={`px-3 py-2 text-right text-xs font-medium ${growth >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                              {growth >= 0 ? '+' : ''}{num(growth)}
                            </td>
                            <td className="px-3 py-2 text-right text-xs">{d.num_posts}</td>
                            <td className="px-3 py-2 text-right text-xs">{num(d.total_views)}</td>
                            <td className="px-3 py-2 text-right text-xs">{num(d.total_engagement)}</td>
                            <td className="px-3 py-2 text-right text-xs">{fmt(d.total_spend)}</td>
                            <td className="px-3 py-2 text-right text-xs text-[#5C6B64]">{fmt(d.longterm_total)}</td>
                            <td className="px-3 py-2 text-right text-xs text-[#5C6B64]">{fmt(d.adhoc_total)}</td>
                            <td className="px-3 py-2 text-right text-xs text-[color:var(--color-primary)]">{cpf != null ? fmt(cpf) : growth === 0 ? '—' : 'Free'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {trendData.length > 1 && (() => {
                      const t = trendData.reduce((a, d) => ({
                        growth: a.growth + ((d.follower_growth ?? (d.closing_followers - d.opening_followers)) || 0),
                        posts: a.posts + (d.num_posts || 0),
                        views: a.views + (d.total_views || 0),
                        engage: a.engage + (d.total_engagement || 0),
                        spend: a.spend + (d.total_spend || 0),
                        lt: a.lt + (d.longterm_total || 0),
                        ad: a.ad + (d.adhoc_total || 0),
                      }), { growth: 0, posts: 0, views: 0, engage: 0, spend: 0, lt: 0, ad: 0 });
                      const tCpf = t.spend > 0 && t.growth > 0 ? Math.round(t.spend / t.growth) : null;
                      return (
                        <tfoot>
                          <tr className="bg-[color:var(--color-primary)]/5 font-semibold border-t-2 border-[color:var(--color-primary)]/20" data-testid="trend-totals-row">
                            <td className="px-3 py-2 text-xs text-[color:var(--color-primary)]">Total</td>
                            <td className="px-3 py-2 text-right text-xs">—</td>
                            <td className={`px-3 py-2 text-right text-xs ${t.growth >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                              {t.growth >= 0 ? '+' : ''}{num(t.growth)}
                            </td>
                            <td className="px-3 py-2 text-right text-xs">{num(t.posts)}</td>
                            <td className="px-3 py-2 text-right text-xs">{num(t.views)}</td>
                            <td className="px-3 py-2 text-right text-xs">{num(t.engage)}</td>
                            <td className="px-3 py-2 text-right text-xs">{fmt(t.spend)}</td>
                            <td className="px-3 py-2 text-right text-xs text-[#5C6B64]">{fmt(t.lt)}</td>
                            <td className="px-3 py-2 text-right text-xs text-[#5C6B64]">{fmt(t.ad)}</td>
                            <td className="px-3 py-2 text-right text-xs text-[color:var(--color-primary)]">{tCpf != null ? fmt(tCpf) : t.growth === 0 ? '—' : 'Free'}</td>
                          </tr>
                        </tfoot>
                      );
                    })()}
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ═══ BUDGETS TAB ═══ */}
        <TabsContent value="budgets">
          <Card className="border-[#E2E8DF] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-[color:var(--color-primary)]" /> Monthly Budgets — {MONTHS[month - 1]} {year}
              </CardTitle>
              <p className="text-[10px] text-[#5C6B64]">Set marketing budget per outlet. Editable anytime by admin.</p>
            </CardHeader>
            <CardContent>
              {budgetsLoading && (
                <div className="space-y-3 py-2" data-testid="budget-list-loading">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-14 rounded-lg bg-[#F3F5F2] animate-pulse" />
                  ))}
                </div>
              )}
              {!budgetsLoading && (
              <div className="space-y-3" data-testid="budget-list">
                {budgets.map(b => {
                  const editVal = budgetEdits[b.outlet_id];
                  const isEditing = editVal !== undefined;
                  const currentVal = isEditing ? editVal : (b.budget > 0 ? String(b.budget) : '');
                  return (
                    <div key={b.outlet_id}
                      className="flex items-center gap-3 p-3 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]/50"
                      data-testid={`budget-row-${b.outlet_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A2421] truncate">{b.outlet_name}</p>
                        {b.updated_at && (
                          <p className="text-[9px] text-[#5C6B64]">Last set: {fmtDateIST(b.updated_at)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#5C6B64]">₹</span>
                        <input type="number" min="0" step="1000" placeholder="0"
                          value={currentVal}
                          onChange={e => setBudgetEdits(p => ({ ...p, [b.outlet_id]: e.target.value }))}
                          className="h-8 w-28 text-sm border border-[#E2E8DF] rounded px-2"
                          data-testid={`budget-input-${b.outlet_id}`} />
                        <button
                          disabled={!isEditing || savingBudget[b.outlet_id]}
                          onClick={() => handleBudgetSave(b.outlet_id)}
                          className={`h-8 px-3 rounded text-xs font-medium ${isEditing ? 'bg-[color:var(--color-primary)] text-white hover:bg-[#1A5240]' : 'bg-[#F3F5F2] text-[#5C6B64]'}`}
                          data-testid={`budget-save-${b.outlet_id}`}>
                          {savingBudget[b.outlet_id] ? '...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
